import { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { BaseModal } from '@/components/shared';
import type { EstadiaCliente, MetodoEntrega } from '../../../models';
import { useEstadiaClienteMutations } from '../../../hooks/data';
import { METODO_ENTREGA_LABELS } from '../equipamentosListPage.definitions';
import { dataInputParaDate } from '../../../utils/dataInput';

interface RecolherParaRaroModalProps {
  isOpen: boolean;
  onClose: () => void;
  estadia: EstadiaCliente;
}

/**
 * Encerra a estadia como "recolhida de volta pra Raro, sem ter sido usada" — registra
 * quando e como foi o recolhimento. Quando o destino é um colaborador (Fase 2), essa
 * informação fica só na Movimentação real (que já tem os mesmos campos), não é duplicada
 * aqui — ver EstadiaCliente.dataRecolhimento/formaRecolhimento.
 */
export default function RecolherParaRaroModal({ isOpen, onClose, estadia }: RecolherParaRaroModalProps) {
  const { atualizarEstadia } = useEstadiaClienteMutations();

  const [data, setData] = useState('');
  const [forma, setForma] = useState<MetodoEntrega | ''>('');
  const [codigoRastreio, setCodigoRastreio] = useState('');

  const handleClose = () => {
    setData('');
    setForma('');
    setCodigoRastreio('');
    onClose();
  };

  const handleConfirmar = () => {
    atualizarEstadia.mutate(
      {
        id: estadia.id,
        status: 'encerrada',
        motivoEncerramento: 'retornado_para_raro',
        dataRecolhimento: data ? Timestamp.fromDate(dataInputParaDate(data)) : Timestamp.now(),
        formaRecolhimento: forma || null,
        codigoRastreioRecolhimento: forma === 'correios' ? codigoRastreio.trim() || null : null,
        dataEncerramento: Timestamp.now(),
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Recolher para a Raro" maxWidth="max-w-md" fullHeight={false}>
      <div className="space-y-4 py-4">
        <p className="text-sm text-gray-600">Equipamento recolhido do Cliente, de volta pro estoque da Raro.</p>

        <div>
          <label className="block text-sm font-medium text-gray-700">Data do recolhimento</label>
          <input type="date" className="input-primary mt-1 w-full" value={data} onChange={(event) => setData(event.target.value)} />
          <p className="mt-1 text-xs text-gray-500">Em branco, usa a data/hora de agora.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Forma de recolhimento</label>
          <select
            className="input-primary mt-1 w-full"
            value={forma}
            onChange={(event) => setForma(event.target.value as MetodoEntrega | '')}
          >
            <option value="">Selecione...</option>
            {(Object.entries(METODO_ENTREGA_LABELS) as [MetodoEntrega, string][]).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {forma === 'correios' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Código de rastreio</label>
            <input
              type="text"
              className="input-primary mt-1 w-full"
              value={codigoRastreio}
              onChange={(event) => setCodigoRastreio(event.target.value)}
            />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleConfirmar} disabled={atualizarEstadia.isPending}>
            {atualizarEstadia.isPending ? 'Salvando...' : 'Confirmar recolhimento'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
